import {catchError, map, takeWhile} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  NgZone,
  OnInit,
} from '@angular/core';
import {AbstractControlComponent} from './abstract-control';
import {ViewTabComponent} from '../viewtab.component';
import {ErrorService} from '../../../error/error.service';
import moment from 'moment-timezone';
import 'moment/min/locales.js';
import {CacheService} from '../../../cache/cache.service';

@Component({
  selector: 'ctrl-datetime',
  template: `
    @if ((getCaughtObservable(isEnabled) | async) !== true) {
      <p>
        {{
          formControl.value | localDate: disabledFormatByType[latestDatePickerType] : false | async
        }}
      </p>
    } @else {
      <div>
        @if (latestDatePickerType === 'date') {
          <mat-form-field>
            <input matInput [matDatepicker]="datePicker" [formControl]="formControl" />
            <mat-datepicker-toggle matSuffix [for]="datePicker"></mat-datepicker-toggle>
            <mat-datepicker #datePicker></mat-datepicker>
          </mat-form-field>
        }

        @if (latestDatePickerType === 'time') {
          <mat-form-field>
            <input matInput [matTimepicker]="timePicker" [formControl]="formControl" />
            <mat-timepicker-toggle matSuffix [for]="timePicker"></mat-timepicker-toggle>
            <mat-timepicker #timePicker></mat-timepicker>
          </mat-form-field>
        }

        @if (latestDatePickerType === 'datetime') {
          <div class="datetime-container">
            <mat-form-field>
              <input matInput [matDatepicker]="datePartPicker" [formControl]="formControl" />
              <mat-datepicker-toggle matSuffix [for]="datePartPicker"></mat-datepicker-toggle>
              <mat-datepicker #datePartPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field>
              <input matInput [matTimepicker]="timePartPicker" [formControl]="formControl" />
              <mat-timepicker-toggle matSuffix [for]="timePartPicker"></mat-timepicker-toggle>
              <mat-timepicker #timePartPicker></mat-timepicker>
            </mat-form-field>
          </div>
        }

        @if (formControl.touched && formControl.hasError('required')) {
          <mat-error>
            <language key="ControlErrorRequired" defaultValue="This field is required."></language>
          </mat-error>
        }
      </div>
    }
  `,
  styleUrls: ['./ctrl-datetime.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlDatetimeComponent extends AbstractControlComponent implements OnInit {
  constructor(
    parent: ViewTabComponent,
    changeDetectorRef: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    private cacheService: CacheService,
  ) {
    super(parent, 'Value', 'Value', changeDetectorRef, _ngZone, errorService);
  }

  latestDatePickerType: string = 'datetime';

  disabledFormatByType = {
    datetime: 'L LT',
    date: 'L',
    time: 'LT',
  };

  protected initValidators(): void {
    super.initValidators();

    if (this._config.propertyExists('DateTimeFormat')) {
      this._config
        .getPropertyValues('DateTimeFormat')
        .pipe(
          map(type => {
            switch (type) {
              default:
              case 'DateTime':
                return 'datetime';
              case 'DateOnly':
                return 'date';
              case 'TimeOnly':
                return 'time';
            }
          }),
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((type: string) => {
          this.latestDatePickerType = type;
        });
    } else {
      this.latestDatePickerType = 'datetime';
    }
  }

  protected formatSource(value: any): any {
    if (value == null || value === '') return null;
    value = moment.utc(value, 'YYYY-MM-DDTHH:mm:ss.SSS');
    value = value.tz(this.cacheService.getCurrentTimeZone());

    return value;
  }

  protected formatTarget(value: moment.Moment | null): any {
    //2017-05-11T17:52:14.453
    if (value === null) return null;
    if (!moment.isMoment(value)) {
      console.error('CtrlDatetime::formatTarget value is no moment!', typeof value, value);
    }

    const dateAsString = value.format('YYYY-MM-DDTHH:mm:ss.SSS');

    const m = moment.tz(dateAsString, this.cacheService.getCurrentTimeZone());
    if (m == null) return null;
    let val = m.toISOString();
    val = val.substring(0, val.length - 1);

    return val;
  }

  private _smallView: boolean = false;

  ngOnInit(): void {
    this._smallView = window.innerWidth < 640;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this._smallView = event.target.innerWidth < 640;
  }

  get container(): string {
    return this._smallView ? 'dialog' : 'inline';
  }

  static getNumberAsString(value: number, length: number): string {
    let formatted: string = '';
    for (let i = length - 1; i > 0; i--) {
      if (value < Math.pow(10, i)) {
        formatted += '0';
      } else {
        break;
      }
    }
    formatted += value;
    return formatted;
  }
}
